import { autorun, makeAutoObservable, runInAction } from 'mobx';
import {
  createScheme,
  updateScheme,
  createSchemeNode,
  deleteSchemeNode,
  getAllSchemes,
  getSchemeById,
  updateSchemeNode,
  deleteScheme,
} from '../../services/scheme.services';
import { toggleActivate } from 'services/protocol.services';
import { appStore } from './app.store';

const NAME = 'schemes-store';
export class SchemesStore {
  schemes = [];
  selectedScheme = null;
  createSchemeState = null;
  updateSchemeState = null;
  isLoading = false;

  constructor() {
    makeAutoObservable(this);

    const storeJSON = localStorage.getItem(NAME);

    if (storeJSON) Object.assign(this, JSON.parse(storeJSON));

    autorun(() => {
      const obj = JSON.parse(JSON.stringify(this));
      obj.isLoading = false;
      localStorage.setItem(NAME, JSON.stringify(obj));
    });

    window.addEventListener('storage', this._updateStore);
  }

  _updateStore = (e) => {
    if (e.key === NAME) {
      const storeJSON = localStorage.getItem(NAME);

      if (storeJSON) Object.assign(this, JSON.parse(storeJSON));
    }
  };

  _dispose = () => {
    window.removeEventListener('storage', this._updateStore);
  };

  _createScheme = (data) => {
    return createScheme(data);
  };

  _updateScheme = (data) => {
    return updateScheme(data);
  };

  _prepareSchemeNodeForApiRequest = (node) => {
    return {
      id: node.id,
      id_scheme: node.id_scheme,
      id_parent: node.id_parent ?? undefined,
      id_protocol: node.id_protocol ?? node.protocol?.id,
      id_quality: node.id_quality ?? node.quality?.id ?? undefined,
      type: node.type,
      ...(node?.type !== 'storage'
        ? {
            objects: node.objects.map((object) => ({
              id: object.id,
              type: object.type,
              ...(typeof object.derived === 'boolean'
                ? {
                    derived: object.derived,
                  }
                : {}),
            })),
            resources: node.resources.map((resource) => ({
              id: resource.id,
              type: resource.type,
            })),
          }
        : {}),
    };
  };

  _updateTreeNode(node, nodeId, newNode) {
    if (node.id === nodeId) {
      return {
        ...node,
        ...newNode,
      };
    }

    if (node.children) {
      return {
        ...node,
        children: node.children.map((child) => this._updateTreeNode(child, nodeId, newNode)),
      };
    }

    return node;
  }

  _addTreeNode(nodes, parentId, newNode) {
    if (!parentId) {
      return [...nodes, { ...newNode, children: [] }];
    }

    return nodes.map((node) => {
      if (node.id === parentId) {
        return {
          ...node,
          children: [...(node.children || []), { ...newNode, children: [] }],
        };
      }

      if (node.children) {
        return {
          ...node,
          children: this._addTreeNode(node.children, parentId, { ...newNode, children: [] }),
        };
      }

      return node;
    });
  }
  _removeTreeNode(nodes, nodeId) {
    return nodes
      .map((node) => {
        if (node.id === nodeId) {
          return node.children || [];
        }

        if (node.children) {
          const updatedChildren = this._removeTreeNode(node.children, nodeId);
          return {
            ...node,
            children: updatedChildren.length > 0 ? updatedChildren : [],
          };
        }

        return node;
      })
      .flat(); // Используем flat, чтобы объединить массивы
  }

  setSchemes = async ({ search = undefined } = {}) => {
    const response = await getAllSchemes({
      search,
      sorting: { field: 'name', type: 'asc' },
    });

    runInAction(() => {
      this.schemes = response.data;
    });
  };

  upsertScheme = async (data) => {
    let response;
    if (this.selectedScheme?.id) {
      response = await this._updateScheme(data);
    } else {
      response = await this._createScheme(data);
    }

    if (response.success) {
      runInAction(() => {
        if (this.selectedScheme?.id) {
          this.schemes = this.schemes.reduce((acc, current) => {
            if (current.id === response.result.data.id)
              current = {
                ...response.result.data,
              };
            acc.push(current);
            return acc;
          }, []);
          this.selectedScheme = { ...this.selectedScheme, ...response.result.data };
        } else this.schemes = [...this.schemes, response.result.data];
      });
    }

    return response;
  };

  removeScheme = async () => {
    const res = await deleteScheme(this.selectedScheme.id);
    if (res.success) {
      runInAction(() => {
        this.schemes = this.schemes.filter((scheme) => scheme.id !== this.selectedScheme.id);
      });
    }

    return res;
  };

  getByIdScheme = async (id) => {
    runInAction(() => {
      this.isLoading = true;
    });
    const response = await getSchemeById(id);
    runInAction(() => {
      this.isLoading = false;
    });
    return { ...response?.data };
  };

  setSelectedScheme = (obj) => {
    obj?.nodes?.sort((a, b) => (a.id > b.id ? 1 : -1));
    runInAction(() => (this.selectedScheme = obj));
  };

  setSchemeUpsertState = (obj) => {
    runInAction(() => {
      if (this.selectedScheme?.id) this.updateSchemeState = obj;
      else this.createSchemeState = obj;
    });
  };

  resetScheme = () =>
    runInAction(() => {
      this.selectedScheme = null;
      this.createSchemeState = null;
      this.updateSchemeState = null;
    });

  upsertSchemeNode = async (node) => {
    let response;
    if (node?.id) {
      response = await updateSchemeNode(node.id, this._prepareSchemeNodeForApiRequest(node));
    } else {
      response = await createSchemeNode(this._prepareSchemeNodeForApiRequest(node));
    }

    if (response.success) {
      runInAction(() => {
        if (node?.id)
          this.selectedScheme = {
            ...this.selectedScheme,
            nodes: this.selectedScheme.nodes.map((oldNode) =>
              this._updateTreeNode(oldNode, node.id, response.result.data),
            ),
          };
        else
          this.selectedScheme = {
            ...this.selectedScheme,
            nodes: this._addTreeNode(
              this.selectedScheme.nodes,
              node.id_parent,
              response.result.data,
            ),
          };
      });
    } else {
      appStore.setAlert(response.error, 'error');
    }

    return response;
  };

  toggleActivateProtocol = async (id) => {
    const response = await toggleActivate(id);
    if (response.success) {
      const updateNodes = (nodes) => {
        nodes.forEach((n) => {
          if (n.protocol?.id === id) {
            n.protocol = response?.result?.data;
          }
          if (n.children && n.children.length > 0) {
            updateNodes(n.children);
          }
        });
      };
      runInAction(() => updateNodes(this.selectedScheme.nodes));
    }
  };

  removeSchemeNode = async (id) => {
    const response = await deleteSchemeNode(id);
    if (response.success) {
      runInAction(
        () =>
          (this.selectedScheme = {
            ...this.selectedScheme,
            nodes: this._removeTreeNode(this.selectedScheme.nodes, response.result.data.id),
          }),
      );
    }
  };
}

export const schemesStore = new SchemesStore();
